import cloneDeep from "lodash/cloneDeep";
import { objectify } from "overline";
import { api } from "#src/api";
import { addToSortableListAtIndex } from "@local/extensions/collections/sortable-list.js";

function getFiles(doc) {
  if (!doc?.data?.pages) return [];
  const present = doc.data.pages.order.reduce((files, pageIndex) => {
    const page = doc.data.pages[pageIndex];
    files[page.name] = true;
    return files;
  }, {});
  return Object.keys(present);
}

async function getFileData(file) {
  const docfile = await api
    .schema("storage")
    .from("objects")
    .select("*")
    .eq("bucket_id", "documents")
    .eq("name", file)
    .single();

  if (docfile.error) return null;

  const sheetData = await api
    .from("dng_sheets")
    .select(
      "*,limits:limits_json,dng_links!dng_links_document_id_sheet_idx_fkey(*)",
    )
    .eq("document_id", docfile.data.id)
    .order("idx");

  const sheets = sheetData.data ? objectify(sheetData.data, "idx") : {};

  let status;
  if (docfile.data.metadata?.mimetype === "application/pdf") {
    const rs = await api
      .schema("suprastore")
      .from("status")
      .select("*")
      .eq("source_bucket", "documents")
      .eq("source_path", file)
      .single();

    if (rs.error) return null;
    status = rs.data?.status || {};
  } else if (docfile.data.metadata?.mimetype.startsWith("image")) {
    status = { 0: { state: "completed" } };
  }

  return { name: file, file: docfile.data, status, sheets };
}

function getProgress(doc, status, filters) {
  if (doc?.ready) return 1;

  const pages = filters?.hidden_pages
    ? doc.data?.pages?.order.filter((id) => !filters.hidden_pages[id])
    : doc.data?.pages?.order;

  if (!pages?.length) return 1;

  const count = pages.length;

  const completed = pages.reduce((c, pageId) => {
    const page = doc.data.pages[pageId];
    if (
      !page.pending &&
      status[page.name]?.status[page.page]?.state === "completed"
    ) {
      return c + 1;
    }
    return c;
  }, 0);

  return completed / count;
}

function getIncomplete(doc, $fileData) {
  const incomplete = doc.data.pages.order.reduce((incomplete, pageId) => {
    const page = doc.data.pages[pageId];
    if (page.pending) {
      incomplete[page.name] = true;
    } else if ($fileData[page.name]?.status[page.page]?.state !== "completed") {
      incomplete[page.name] = true;
    }
    return incomplete;
  }, {});

  return incomplete;
}

function backoff(timeoutInterval) {
  return Math.min(timeoutInterval * 2, 10000);
}

async function pollUntilComplete(fileData, getDoc, updateDoc, signal) {
  let doc = getDoc();
  let timeoutInterval = 1000;

  let $fileData;
  fileData.subscribe((v) => ($fileData = v));

  let progress = getProgress(doc, $fileData);

  if (progress >= 1) {
    if (!doc.ready) {
      updateDoc({ id: doc.id, diff: { ready: true } });
    }

    return;
  }

  try {
    while (progress < 1) {
      signal.throwIfAborted();
      const incomplete = getIncomplete(doc, $fileData);
      const s = await Promise.all(
        Object.keys(incomplete).map((f) => getFileData(f)),
      );
      signal.throwIfAborted();

      let bo = true;

      s.forEach((st) => {
        if (st?.file?.name) {
          if (st.file.name === doc.data.thumbnail?.objectName) {
            const fd = $fileData[st.file.name];
            const fn = doc.data.thumbnail.path?.split("/").slice(-1)?.[0];
            const page = fn?.match(/(\d+)\.tiff$/);

            if (fd && page) {
              const state = st.status[page[1]]?.state;
              const prevstate = fd?.status?.[page[1]]?.state;

              if (state === "completed" && prevstate !== "completed") {
                updateDoc();
              }
            }
          }
          if (
            Object.keys(st.status).some(
              (k) =>
                st.status[k]?.state !==
                $fileData[st.file.name]?.status[k]?.state,
            )
          ) {
            bo = false;
          }

          fileData.update((fd) => {
            fd[st.file.name] = st;
            return fd;
          });
        }
      });

      // Find any pending pages that are now complete
      const counts = {};
      doc.data.pages.order.forEach((pid) => {
        const page = doc.data.pages[pid];
        if (page?.pending && $fileData[page.name]) {
          const sheets = Object.keys($fileData[page.name].sheets);
          if (sheets.length) {
            counts[pid] = sheets.length;
          }
        }
      });

      if (Object.keys(counts).length) {
        doc = getDoc();
        const data = cloneDeep(doc.data);

        Object.keys(counts)
          .toReversed()
          .forEach((pid) => {
            const page = data.pages[pid];
            delete page.pending;
            const count = counts[pid];

            const newIds =
              count > 1
                ? Array.from({ length: count - 1 }, (_) => crypto.randomUUID())
                : [];
            const newPages = newIds.map((id, i) => ({
              ...page,
              id,
              page: i + 1,
            }));

            if (newPages.length) {
              const pageIndex = data.pages.order.indexOf(pid);
              addToSortableListAtIndex(data.pages, pageIndex + 1, ...newPages);
            }
          });

        updateDoc({ id: doc.id, diff: { data } });
      }

      if (bo) {
        timeoutInterval = backoff(timeoutInterval);
      } else {
        timeoutInterval = 2000;
      }

      await new Promise((resolve) => setTimeout(resolve, timeoutInterval));
      doc = getDoc();
      progress = getProgress(doc, $fileData);
    }

    if (!doc.ready) {
      updateDoc({ id: doc.id, diff: { ready: true } });
    }
  } catch (error) {
    if (error.name !== "AbortError") {
      throw error;
    }
  }
}

export { getFiles, getFileData, getProgress, pollUntilComplete };
